/**
 * AUTH STORE — Zustand + immer + devtools + persist
 *
 * HOW THIS AUTH SYSTEM WORKS
 * ──────────────────────────
 * 1. LOGIN
 *    - Call your auth API and get back { user, accessToken }.
 *    - Call `useAuthStore.getState().setLoginState({ user, accessToken })`.
 *    - The token is persisted to localStorage and attached to every API request
 *      by `src/lib/api-client.ts` (request interceptor reads `getAuthHeader()`).
 *
 * 2. PRIVILEGE SYSTEM (optional — remove if not needed)
 *    - `user.privilege` is an array of fine-grained permission strings,
 *      e.g. `['si-all', 'la-park-view']`.
 *    - `hasPrivilege(p)` checks direct match + wildcard (`si-all` grants all `si-*`).
 *    - `hasSystemAccess(system)` checks if the user can access an entire module.
 *    - `hasResourceAccess(type, id)` checks ownership of specific records.
 *    - REPLACE or REMOVE these methods if your project uses a different RBAC model
 *      (e.g., role-based without fine-grained privileges, or server-side checks).
 *
 * 3. FORCE LOGOUT (401 / 403 guard)
 *    - `api-client.ts` calls `setForceLogout(true)` on the first 401/403 whose
 *      local token is dead (missing/expired `exp`) or which is a gateway-level
 *      denial — application 401/403s with a live token never trigger it.
 *    - `ForceLogoutDialog` watches `forceLogout` and shows a modal.
 *    - After user acknowledges, call `logout()` + `clearForceLogout()`.
 *    - This singleton pattern prevents duplicate dialogs when multiple requests fail.
 *
 * 4. PERSISTED FIELDS
 *    - Only `user`, `accessToken`, `isAuthenticated`, `availableSystems` survive
 *      a page refresh (partialize). Do NOT add sensitive computed state here.
 *
 * QUICKSTART: Replace the `User`, `Privilege`, `SystemCode` types below with
 * your own API response shape, then wire `setLoginState` to your login mutation.
 */

import { create } from 'zustand'
import { createJSONStorage, devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

// ---------------------------------------------------------------------------
// Domain types — replace with your own
// ---------------------------------------------------------------------------

export type SystemCode = string // e.g. 'si' | 'ec' | 'la'
export type Privilege = string // e.g. 'si-all' | 'la-park-view'

export interface User {
  id: string | number
  name: string
  email: string
  role_name: string
  privilege: Privilege[]
  // Add resource-ownership arrays if needed:
  // project_id?: number[]
  // park_id?: number[]
}

// ---------------------------------------------------------------------------
// Store interface
// ---------------------------------------------------------------------------

interface AuthState {
  user: User | null
  accessToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  availableSystems: SystemCode[]
  forceLogout: boolean
  isLoggingOut: boolean
}

interface AuthStore extends AuthState {
  logout: () => Promise<void>
  updateUser: (update: Partial<User>) => void
  clearError: () => void
  setLoginState: (data: { user: User; accessToken: string }) => void
  setError: (error: string) => void
  setForceLogout: (show: boolean) => void
  clearForceLogout: () => void
  hasPrivilege: (privilege: Privilege) => boolean
  hasAnyPrivilege: (privileges: Privilege[]) => boolean
  hasAllPrivileges: (privileges: Privilege[]) => boolean
  hasSystemAccess: (system: SystemCode) => boolean
  getAuthHeader: () => string | null
}

// ---------------------------------------------------------------------------
// Store implementation
// ---------------------------------------------------------------------------

export const useAuthStore = create<AuthStore>()(
  devtools(
    persist(
      immer<AuthStore>((set, get) => ({
        user: null,
        accessToken: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
        availableSystems: [],
        forceLogout: false,
        isLoggingOut: false,

        async logout() {
          set((s) => {
            s.isLoggingOut = true
          })
          try {
            // TODO: call your backend logout endpoint if needed
            // await api.post('/auth/logout')
          } catch (error) {
            console.warn('Logout error:', error)
          } finally {
            set((s: AuthState) => {
              s.user = null
              s.accessToken = null
              s.isAuthenticated = false
              s.availableSystems = []
              s.error = null
              s.forceLogout = false
              s.isLoggingOut = false
            })
          }
        },

        setForceLogout(show: boolean) {
          set((s: AuthState) => {
            s.forceLogout = show
          })
        },

        clearForceLogout() {
          set((s: AuthState) => {
            s.forceLogout = false
          })
        },

        updateUser(update: Partial<User>) {
          set((s: AuthState) => {
            if (s.user) {
              Object.assign(s.user, update)
            }
          })
        },

        clearError() {
          set((s: AuthState) => {
            s.error = null
          })
        },

        setLoginState(data: { user: User; accessToken: string }) {
          set((s: AuthState) => {
            s.user = data.user
            s.accessToken = data.accessToken
            s.isAuthenticated = true
            s.isLoading = false
            s.error = null
            s.forceLogout = false
          })
        },

        setError(errorMessage: string) {
          set((s: AuthState) => {
            s.error = errorMessage
            s.isLoading = false
            s.isAuthenticated = false
          })
        },

        // Privilege helpers — simplify or remove if not using fine-grained RBAC
        hasPrivilege(privilege: Privilege) {
          const u = get().user
          if (!u?.privilege) {
            return false
          }
          return u.privilege.includes(privilege)
        },

        hasAnyPrivilege(privileges: Privilege[]) {
          return privileges.some((p: Privilege) => get().hasPrivilege(p))
        },

        hasAllPrivileges(privileges: Privilege[]) {
          return privileges.every((p: Privilege) => get().hasPrivilege(p))
        },

        hasSystemAccess(system: SystemCode) {
          const u = get().user
          if (!u) {
            return false
          }
          return u.privilege.some((p: string) => p.startsWith(`${system}-`))
        },

        getAuthHeader() {
          const token = get().accessToken
          return token ? `Bearer ${token}` : null
        },
      })),
      {
        name: 'auth-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (s: AuthStore) => ({
          user: s.user,
          isAuthenticated: s.isAuthenticated,
          accessToken: s.accessToken,
          availableSystems: s.availableSystems,
        }),
      },
    ),
    { name: 'AuthStore' },
  ),
)

// Helper functions (used by api-client.ts)
export const getAccessToken = () => useAuthStore.getState().accessToken
export const getAuthHeader = () => useAuthStore.getState().getAuthHeader()
